/** SUNTRONIX ONE — Customer Playbook Matrix.
 *  Scalable list of customers under a master client with playbook facets:
 *  Country · ABM · MDM · Autopilot · AppleCare+ · Reuse · Warehouse ·
 *  Erasure · Recovery · Repair · ITAD · Completeness.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ArrowRight, ShieldCheck, AlertCircle, Plus, Eye } from "lucide-react";
import { usePreview } from "@/context/PreviewContext";

const MDM_LABEL = {
  intune: "Microsoft Intune", jamf_pro: "Jamf Pro", kandji: "Kandji",
  mosyle: "Mosyle", workspace_one: "Workspace ONE", soti: "SOTI",
};
const badge = (v, opts = {}) => {
  if (v === "yes" || v === true) return <span className="text-emerald-700">Yes</span>;
  if (v === "no" || v === false) return <span className="text-slate-500">No</span>;
  if (v === "allowed") return <span className="text-emerald-700">Allowed</span>;
  if (v === "not_allowed") return <span className="text-red-700">No</span>;
  if (v === "approval_required") return <span className="text-amber-700">Approval</span>;
  if (!v) return <span className="text-slate-400">—</span>;
  return <span className="text-slate-700">{opts.map?.[v] || v}</span>;
};

export default function CustomerMatrix() {
  const navigate = useNavigate();
  const { start: startPreview } = usePreview();
  const [masterClients, setMasterClients] = useState([]);
  const [selectedMc, setSelectedMc] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [mdm, setMdm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/master-clients").then(({ data }) => {
      const items = data.items || [];
      setMasterClients(items);
      setSelectedMc((prev) => prev || (items[0]?.id ?? null));
    });
  }, []);

  useEffect(() => {
    if (!selectedMc) return;
    setLoading(true);
    Promise.all([
      api.get(`/master-clients/${selectedMc}/dashboard`),
      api.get(`/master-clients/${selectedMc}/customers`, {
        params: { q: q || undefined, country: country || undefined, mdm: mdm || undefined },
      }),
    ]).then(([d, c]) => {
      setDashboard(d.data);
      setRows(c.data.items || []);
      setTotal(c.data.total || 0);
    }).finally(() => setLoading(false));
  }, [selectedMc, q, country, mdm]);

  const kpis = useMemo(() => {
    if (!dashboard) return [];
    return [
      { label: "Customers", value: dashboard.customers },
      { label: "Assets", value: dashboard.assets },
      { label: "Employees", value: dashboard.employees },
      { label: "Playbooks", value: dashboard.tech_env_profiles },
      { label: "Profile gaps", value: (dashboard.gaps?.no_tech_env || 0) + (dashboard.gaps?.mdm_unknown || 0) },
    ];
  }, [dashboard]);

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="customer-matrix">
      <PageHeader
        eyebrow="Customer 360"
        title="Customer Playbook Matrix"
        context={dashboard?.master_client?.name ? `Master client: ${dashboard.master_client.name}` : "Select a master client"}
        actions={selectedMc && (
          <button
            onClick={() => navigate(`/customer/new?master_client_id=${selectedMc}`)}
            className="st-btn st-btn-primary st-btn-sm"
            data-testid="customer-matrix-onboard-new"
          >
            <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Onboard customer
          </button>
        )}
        testId="customer-matrix-header"
      />

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <Select value={selectedMc || ""} onValueChange={setSelectedMc}>
          <SelectTrigger className="md:w-72" data-testid="customer-matrix-mc-select">
            <SelectValue placeholder="Master client…" />
          </SelectTrigger>
          <SelectContent>
            {masterClients.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1 relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.6} />
          <Input className="pl-8" placeholder="Search customer name or code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-matrix-search" />
        </div>
        <Input className="md:w-24" placeholder="Country" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="customer-matrix-country" />
        <Input className="md:w-40" placeholder="MDM (intune, jamf_pro…)" value={mdm} onChange={(e) => setMdm(e.target.value)} data-testid="customer-matrix-mdm" />
      </div>

      {kpis.length > 0 && (
        <Section title="Portfolio" hint="Roll-up under this master client." testId="customer-matrix-portfolio">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {kpis.map((k) => (
              <div key={k.label} className="st-panel p-3" data-testid={`portfolio-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">{k.label}</div>
                <div className="text-2xl font-heading font-semibold mt-1">{k.value ?? "—"}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title={`Customers · ${total}`} hint="Click a row to open the customer's Technology Environment." testId="customer-matrix-list">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[color:var(--st-surface-alt)] text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">
              <tr>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Country</th>
                <th className="text-left px-3 py-2">ABM</th>
                <th className="text-left px-3 py-2">MDM</th>
                <th className="text-left px-3 py-2">Autopilot</th>
                <th className="text-left px-3 py-2">AppleCare+</th>
                <th className="text-left px-3 py-2">Reuse</th>
                <th className="text-left px-3 py-2">Warehouse</th>
                <th className="text-left px-3 py-2">Erasure</th>
                <th className="text-left px-3 py-2">Recovery</th>
                <th className="text-left px-3 py-2">Repair</th>
                <th className="text-left px-3 py-2">ITAD</th>
                <th className="text-left px-3 py-2">%</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={15} className="p-6 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={15} className="p-6 text-center text-slate-400">No customers match the filters.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}
                  className="border-t border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)] cursor-pointer"
                  onClick={() => navigate(`/customer/${r.id}/tech-env`)}
                  data-testid={`customer-matrix-row-${r.code}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[11px] text-slate-500">{r.code}</div>
                  </td>
                  <td className="px-3 py-2">{r.country || "—"}</td>
                  <td className="px-3 py-2">{badge(r.abm)}</td>
                  <td className="px-3 py-2">{badge(r.primary_mdm, { map: MDM_LABEL })}</td>
                  <td className="px-3 py-2">{badge(r.autopilot)}</td>
                  <td className="px-3 py-2">{badge(r.applecare_plus)}</td>
                  <td className="px-3 py-2">{badge(r.reuse_allowed)}</td>
                  <td className="px-3 py-2">{badge(r.warehouse_suntronix)}</td>
                  <td className="px-3 py-2">{badge(r.erasure_required)}</td>
                  <td className="px-3 py-2">{badge(r.recovery_enabled)}</td>
                  <td className="px-3 py-2">{badge(r.repair_allowed)}</td>
                  <td className="px-3 py-2">{badge(r.itad_enabled)}</td>
                  <td className="px-3 py-2 font-mono">
                    {r.profile_completeness != null ? `${r.profile_completeness}%` : (
                      <span className="inline-flex items-center gap-1 text-amber-600 text-[11px]">
                        <AlertCircle className="h-3 w-3" strokeWidth={1.8} /> No env
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/customer/${r.id}/tax`); }}
                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 mr-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-800"
                      data-testid={`tax-editor-${r.code}`}
                      title="Tax registrations"
                    >
                      Tax
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); startPreview(r.id); }}
                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded border border-yellow-500 bg-yellow-50 hover:bg-yellow-100 text-yellow-900"
                      data-testid={`preview-as-customer-${r.code}`}
                    >
                      <Eye className="h-3 w-3" strokeWidth={2} /> Preview
                    </button>
                  </td>
                  <td className="px-3 py-2 text-slate-400"><ArrowRight className="h-4 w-4" strokeWidth={1.6} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
